import React, { useState, useRef, useEffect, useCallback } from "react";
import PaginatedTable from "../../shared/PaginatedTable";
import CourseSchedulesTableActions from "./CourseSchedulesTableActions";
import CourseSchedulesTableFilters from "./CourseSchedulesTableFilters";
import CourseSchedulesTableHeader from "./CourseSchedulesTableHeader";
import CourseSchedulesTableRow from "./CourseSchedulesTableRow";
import CourseScheduleAttendanceModal from "./CourseScheduleAttendanceModal";
import LocationDetailsModal from "../locations/location/LocationDetailsModal";

import EduApi from "../../../services/resources/EduApi";
import axios from "axios";

export default function CourseSchedulesTable({
  eduChabadHouses,
  eduProgram,
  mobileMode,
  systemLists: { eduCourseSemesters = [] } = {},
}) {
  const [courseSchedules, setCourseSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [results, setResults] = useState(12);
  const [selectedCourseSchedule, setSelectedCourseSchedule] = useState(null);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceCourseSchedule, setAttendanceCourseSchedule] =
    useState(null);
  const [showLocationDetailsModal, setShowLocationDetailsModal] =
    useState(false);
  const [sortBy, setSortBy] = useState("date");
  const [success, setSuccess] = useState(true);
  const [totalCourseSchedules, setTotalCourseSchedules] = useState(0);
  const [totalStudentsEnrolled, setTotalStudentsEnrolled] = useState(0);

  const apiSignal = useRef(axios.CancelToken.source());
  const filtersRef = useRef({ keyword: "" });

  useEffect(() => {
    const signal = apiSignal.current;
    return () => {
      signal.cancel();
    };
  }, []);

  const getCourseSchedules = useCallback(
    (page = 1, results = 12, sortBy = "date") => {
      setLoading(true);
      setPage(page);
      setResults(results);
      setSortBy(sortBy);
      setSuccess(true);

      EduApi.getEduProgramCourseSchedules(
        apiSignal.current.token,
        eduProgram.id,
        page,
        results,
        sortBy,
        filtersRef.current,
      )
        .then(
          ({
            courseSchedules,
            totalCourseSchedules,
            totalStudentsEnrolled,
          }) => {
            setCourseSchedules(courseSchedules);
            setLoading(false);
            setTotalCourseSchedules(totalCourseSchedules);
            setTotalStudentsEnrolled(totalStudentsEnrolled);
          },
        )
        .catch((err) => {
          if (!axios.isCancel(err)) {
            setCourseSchedules([]);
            setLoading(false);
            setSuccess(false);
            setTotalCourseSchedules(0);
            setTotalStudentsEnrolled(0);
          }
        });
    },
    [eduProgram.id],
  );

  const filterCourseSchedules = useCallback(
    (newFilters) => {
      filtersRef.current = newFilters;
      getCourseSchedules(1, results, sortBy);
    },
    [getCourseSchedules, results, sortBy],
  );

  const getCourseSchedulesForExport = useCallback(async () => {
    const courseSchedulesForExport = await EduApi.getEduProgramCourseSchedules(
      apiSignal.current.token,
      eduProgram.id,
      undefined,
      undefined,
      sortBy,
      filtersRef.current,
      true,
    );

    return courseSchedulesForExport.courseSchedules;
  }, [eduProgram.id, sortBy]);

  const toggleViewDetails = useCallback((show, courseSchedule = null) => {
    setSelectedCourseSchedule(courseSchedule);
    setShowLocationDetailsModal(show);
  }, []);

  const openAttendanceModal = useCallback((courseSchedule) => {
    setAttendanceCourseSchedule(courseSchedule);
    setShowAttendanceModal(true);
  }, []);

  const closeAttendanceModal = useCallback(() => {
    setShowAttendanceModal(false);
    setAttendanceCourseSchedule(null);
  }, []);

  return (
    <div>
      <div className="flex flex-justify-space flex-align-center">
        <p className="xxl-text fw-500">Schedules</p>
        <CourseSchedulesTableActions
          eduProgram={eduProgram}
          getCourseSchedulesForExport={getCourseSchedulesForExport}
        />
      </div>
      <div className="courses-table">
        <PaginatedTable
          filterComponent={
            <CourseSchedulesTableFilters
              applyFilters={filterCourseSchedules}
              eduChabadHouses={eduChabadHouses}
              eduCourseSemesters={eduCourseSemesters}
              eduProgram={eduProgram}
            />
          }
          loading={loading}
          loadData={getCourseSchedules}
          mobileMode={mobileMode}
          page={page}
          records={courseSchedules}
          renderHeader={() => (
            <CourseSchedulesTableHeader
              totalStudentsEnrolled={totalStudentsEnrolled}
            />
          )}
          renderRow={(courseSched, index) => (
            <CourseSchedulesTableRow
              key={index}
              courseSchedule={courseSched}
              onClickMarkAttendance={() => openAttendanceModal(courseSched)}
              onViewDetails={() => toggleViewDetails(true, courseSched)}
            />
          )}
          results={results}
          showResultsCount={true}
          sortBy={sortBy}
          success={success}
          totalCount={totalCourseSchedules}
        />
      </div>

      {showAttendanceModal && (
        <CourseScheduleAttendanceModal
          courseSchedule={attendanceCourseSchedule}
          onClose={closeAttendanceModal}
        />
      )}

      <LocationDetailsModal
        close={() => toggleViewDetails(false)}
        locationId={
          selectedCourseSchedule &&
          selectedCourseSchedule.eduChabadHouseEnrollmentID
        }
        show={showLocationDetailsModal}
      />
    </div>
  );
}
